// packages/ui/src/billing/MerchantPicker.tsx
//
// Shared merchant picker — modal with debounced search by name/id/email.
// Replaces the previous "load all merchants into a <select>" pattern that
// doesn't scale past ~hundreds (10K+ merchants would freeze the dropdown).
//
// Bug 17 (debug-bill iter 10, 2026-05-04): InvoiceListPage / CreditMemoListPage /
// ManualGeneratePage all bulk-fetched merchants into a flat <select>. With
// 10K+ merchants this is unusable. This component lazy-fetches via
// GET /admin/merchants?q=X&limit=20&billing_mode=INVOICE on each search.
//
// Usage:
//   <MerchantPicker
//     value={merchantId}
//     valueLabel={merchantName}
//     onChange={(id, name) => setMerchantId(id)}
//     billingMode="INVOICE"
//   />

import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { apiClient } from '@printway/shared/api';

export interface MerchantPickerOption {
  id: string;
  /** Display name (or fallback to email/id when name is empty). */
  name: string;
  email?: string | null;
  billing_mode?: string | null;
  /** Optional logo URL — when absent the picker renders an initials chip. */
  logo_url?: string | null;
}

// Phase 8.2 debug 2026-05-23 bug D — small initials avatar so the picker
// rows surface a visual anchor even when the merchant has no logo URL.
// Background colour is deterministic from the id so the same merchant
// always renders the same colour across re-fetches.
const AVATAR_COLORS = [
  'bg-rose-100 text-rose-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-indigo-100 text-indigo-700',
  'bg-teal-100 text-teal-700',
  'bg-fuchsia-100 text-fuchsia-700',
] as const;

function pickAvatarColour(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) & 0xffff;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]!;
}

function initialsFor(name: string): string {
  const tokens = name.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return '?';
  if (tokens.length === 1) return tokens[0]!.slice(0, 2).toUpperCase();
  return (tokens[0]![0]! + tokens[tokens.length - 1]![0]!).toUpperCase();
}

export interface MerchantPickerProps {
  value: string | null;
  /** Caller-controlled label so the trigger shows the selected name even
   *  when the modal hasn't been opened. Pass null/undefined for empty. */
  valueLabel?: string | null;
  onChange: (id: string | null, label: string | null) => void;
  /** Optional BE-side filter — only INVOICE-mode merchants surface. */
  billingMode?: 'INVOICE' | 'PREPAID';
  placeholder?: string;
  disabled?: boolean;
  /** Forwarded to the trigger button for E2E test addressing. */
  testId?: string;
  /** Tailwind extension for the trigger button. */
  className?: string;
  /** i18n labels — consumer passes t()-translated copy so this shared,
   *  i18n-agnostic component never hardcodes locale strings. English
   *  fallbacks keep it usable when a caller omits them (no mixed-locale). */
  labels?: {
    /** aria-label + modal heading, e.g. "Pick merchant". */
    title?: string;
    /** Search input placeholder. */
    searchPlaceholder?: string;
    /** Shown while a search request is in flight. */
    loading?: string;
    /** Empty state after a query returns nothing. */
    noResults?: string;
    /** Prompt before the user has typed anything. */
    prompt?: string;
  };
}

interface AdminMerchantListResponse {
  merchants: Array<{
    id: string;
    name?: string | null;
    email?: string | null;
    billing_mode?: string | null;
    logo_url?: string | null;
    avatar_url?: string | null;
  }>;
  next_cursor?: string | null;
}

interface AdminMerchantListEnvelope {
  data?: AdminMerchantListResponse;
}

function unwrap(payload: unknown): AdminMerchantListResponse | null {
  if (!payload || typeof payload !== 'object') return null;
  const env = payload as AdminMerchantListEnvelope;
  if (env.data && typeof env.data === 'object') return env.data;
  return payload as AdminMerchantListResponse;
}

export function MerchantPicker({
  value,
  valueLabel,
  onChange,
  billingMode,
  placeholder,
  disabled,
  testId,
  className,
  labels,
}: MerchantPickerProps): JSX.Element {
  const lbl = {
    title: labels?.title ?? 'Pick merchant',
    searchPlaceholder: labels?.searchPlaceholder ?? 'Search by name, email, or ID…',
    loading: labels?.loading ?? 'Loading…',
    noResults: labels?.noResults ?? 'No matching merchant found',
    prompt: labels?.prompt ?? 'Type to search merchant by name / email / ID',
  };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<MerchantPickerOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Debounce 300ms — keeps key-press latency low without spamming BE.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch merchants when modal open or search changes.
  const reqIdRef = useRef(0);
  useEffect(() => {
    if (!open) return;
    const reqId = ++reqIdRef.current;
    const params = new URLSearchParams();
    params.set('limit', '20');
    if (billingMode) params.set('billing_mode', billingMode);
    if (debouncedSearch) params.set('q', debouncedSearch);
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const c = apiClient() as unknown as {
          GET: (
            p: string,
          ) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        };
        const { data, response } = await c.GET(
          `/api/v1/admin/merchants?${params.toString()}`,
        );
        if (reqId !== reqIdRef.current) return; // raced
        if (!response.ok) {
          setError(`HTTP ${response.status}`);
          setOptions([]);
          return;
        }
        const body = unwrap(data);
        const list = body?.merchants ?? [];
        setOptions(
          list.map((m) => ({
            id: m.id,
            name: m.name?.trim() || m.email?.trim() || m.id,
            email: m.email ?? null,
            billing_mode: m.billing_mode ?? null,
            logo_url: m.logo_url ?? m.avatar_url ?? null,
          })),
        );
      } catch (err) {
        if (reqId !== reqIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Network error');
        setOptions([]);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    })();
  }, [open, debouncedSearch, billingMode]);

  const triggerLabel = useMemo(() => {
    if (value && valueLabel) return valueLabel;
    if (value) return value;
    return placeholder ?? 'Select merchant…';
  }, [value, valueLabel, placeholder]);

  function pick(opt: MerchantPickerOption): void {
    onChange(opt.id, opt.name);
    setOpen(false);
  }

  function clear(e: React.MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    onChange(null, null);
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        data-testid={testId ?? 'merchant-picker-trigger'}
        className={
          className ??
          'inline-flex h-10 w-full items-center justify-between gap-2 rounded-md border border-pw-border bg-white px-3 text-left text-sm text-pw-text hover:bg-pw-surface disabled:cursor-not-allowed disabled:opacity-50'
        }
      >
        <span
          className={
            value ? 'truncate' : 'truncate text-pw-text-muted'
          }
        >
          {triggerLabel}
        </span>
        <span className="flex items-center gap-1">
          {value ? (
            <button
              type="button"
              onClick={clear}
              className="text-xs text-pw-text-muted hover:text-pw-text"
              aria-label="Clear merchant"
            >
              ×
            </button>
          ) : null}
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-4 w-4 text-pw-text-muted"
          >
            <path
              d="M11 19a8 8 0 1 1 5.3-13.96l3.4-3.4 1.42 1.42-3.4 3.4A8 8 0 0 1 11 19zm0-2a6 6 0 1 0 0-12 6 6 0 0 0 0 12z"
              fill="currentColor"
            />
          </svg>
        </span>
      </button>

      {open ? (
        <div
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={lbl.title}
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-6 sm:p-12"
        >
          <div
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
            }}
            onClick={(e) => e.stopPropagation()}
            role="document"
            className="flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl"
          >
            <div className="border-b border-pw-border px-4 py-3 text-sm font-semibold text-pw-text">
              {lbl.title} {billingMode ? `(${billingMode} mode)` : ''}
            </div>
            <div className="border-b border-pw-border p-3">
              <input
                type="text"
                placeholder={lbl.searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                data-testid="merchant-picker-search"
                className="h-9 w-full rounded-md border border-pw-border bg-white px-3 text-sm text-pw-text outline-none focus:ring-2 focus:ring-pw-primary-strong/40"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  {lbl.loading}
                </div>
              ) : error ? (
                <div className="px-4 py-6 text-center text-sm text-red-600">
                  {error}
                </div>
              ) : options.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  {debouncedSearch ? lbl.noResults : lbl.prompt}
                </div>
              ) : (
                <ul className="divide-y divide-pw-border">
                  {options.map((opt) => (
                    <li key={opt.id}>
                      <button
                        type="button"
                        onClick={() => pick(opt)}
                        data-testid={`merchant-picker-option-${opt.id}`}
                        className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-pw-surface"
                      >
                        {opt.logo_url ? (
                          <img
                            src={opt.logo_url}
                            alt=""
                            className="h-8 w-8 flex-shrink-0 rounded-full border border-pw-border bg-white object-cover"
                          />
                        ) : (
                          <span
                            aria-hidden="true"
                            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${pickAvatarColour(opt.id)}`}
                          >
                            {initialsFor(opt.name)}
                          </span>
                        )}
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <span className="truncate text-sm font-medium text-pw-text">
                            {opt.name}
                          </span>
                          <span className="truncate text-xs text-pw-text-muted">
                            {opt.email ? `${opt.email} · ` : ''}
                            {opt.id}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-end border-t border-pw-border px-4 py-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-9 items-center rounded-md border border-pw-border bg-white px-4 text-sm font-medium text-pw-text hover:bg-pw-surface"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
